/**
 * Directory Search Service (DSS) – Webex phone/extension search via Search REST API.
 * Single responsibility: call DSS Search API (POST → 202, results via Mercury event:directory.search)
 * and return directory results as user-shaped objects. Used by MsTeamsUserService for dialpad/extension search.
 * Replaces dss.lookupByPhoneNumbers with direct Search API so extension search works without SDK DSS plugin.
 */

import config from '../../../src/config';
import { getLocalStorageItem } from '../../packages/logger/src/LocalStorage';
import { Logger } from '../../packages/logger/src/Logger';
import { validateCatalogUrl } from './catalogUrlValidator';
import { WebexTokenEnum } from './MsTeamsAuthenticationService';

/** Phone number with type from DSS (e.g. work, mobile, work_extension). */
export interface DirectorySearchPhone {
  type: string;
  value: string;
}

/** Result shape for directory lookup (compatible with IGraphUser). */
export interface DirectorySearchUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  type?: string;
  phones?: string[];
  /** When present, DSS returned typed phones (work, mobile, work_extension); use for correct Extension vs Mobile label. */
  phoneNumbersWithType?: DirectorySearchPhone[];
  fromDss: true;
  /** Set for a calling workspace so the dialpad can list it under Devices. */
  isWorkspaceDevice?: true;
  /** DSS response photos[].value (thumbnail preferred); used for avatar when present, else fallback to Graph. */
  avatarUrl?: string;
}

/** In-memory cache for U2C directory search base URL (no localStorage). */
let dssBaseUrlCache: string | null = null;

/**
 * Resolves directory search base URL from U2C catalog (cached in memory). No config fallback.
 */
async function getDirectoryBaseUrlAsync(): Promise<string> {
  if (dssBaseUrlCache) return dssBaseUrlCache;
  const orgId = getOrgIdFromContext();
  const accessToken = getLocalStorageItem<string>(WebexTokenEnum.AUTH_TOKEN);
  try {
    const catalogUrl = `${config.WEBEX_U2C_BASE_URL}/org/${orgId}/catalog?services=directorySearch&format=servicelist`;
    const res = await fetch(catalogUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      Logger.info(`[DSS] U2C catalog for directorySearch failed: ${res.status}`);
      return '';
    }
    const data = (await res.json()) as { services?: Array<{ serviceUrls?: Array<{ baseUrl?: string }> }> };
    const services = data?.services ?? [];
    const baseUrls = services.flatMap((s) => (s.serviceUrls ?? []).map((u) => u.baseUrl).filter(Boolean)) as string[];
    if (baseUrls.length > 0) {
      try {
        dssBaseUrlCache = validateCatalogUrl(baseUrls[0]);
        return dssBaseUrlCache;
      } catch (urlErr: any) {
        Logger.info(`[DSS] U2C directorySearch URL rejected: ${urlErr?.message ?? urlErr}`);
        return '';
      }
    }
  } catch (err: any) {
    Logger.info(`[DSS] Could not get directory search URL from U2C: ${err?.message ?? err}`);
  }
  return '';
}

function getWebexInstance(): any {
  if (typeof window === 'undefined') return undefined;
  if ((window as any).webex) return (window as any).webex;
  return undefined;
}

function getOrgIdFromContext(): string {
  return getLocalStorageItem<string>(WebexTokenEnum.ORG_ID) ?? '';
}

/** Extract thumbnail or first photo URL from DSS entity.photos array. */
function getDssPhotoUrl(photos: any): string | undefined {
  const arr = Array.isArray(photos) ? photos : [];
  const thumbnail = arr.find((p: any) => p?.type === 'thumbnail' && typeof p?.value === 'string');
  if (thumbnail?.value) return thumbnail.value;
  const first = arr.find((p: any) => typeof p?.value === 'string');
  return first?.value;
}

/** Normalize DSS phone type to our label: work_extension / work-extension -> extension, else pass through. */
function normalizeDssPhoneType(t: string): string {
  const normalized = (t ?? 'work').toLowerCase();
  if (normalized === 'work_extension' || normalized === 'work-extension') return 'extension';
  if (normalized === 'facsimile' || normalized === 'facsimiletelephonenumber') return 'fax';
  return normalized;
}

/** Phone types that map to Graph businessPhones (office/work lines only — not fax/home/mobile). */
function isBusinessPhoneType(type: string): boolean {
  return type === 'work' || type === 'extension';
}

/**
 * Dialpad search types. ROOM plus includeCommonAreaPhones matches the Webex app
 * device search so a Webex Calling common-area workspace can be returned.
 * Directory Search Service - Workspaces (Confluence 286858454).
 */
const DIRECTORY_SEARCH_REQUESTED_TYPES = [
  'PERSON',
  'CALLING_SERVICE',
  'EXTERNAL_CALLING',
  'AN_ORG_CUSTOM',
  'ROOM',
] as const;

export function buildDirectorySearchRequestBody(
  query: string,
  requestLimit: number,
  requestId: string,
  queryTypes: DirectoryQueryType[]
): Record<string, unknown> {
  return {
    queryString: query,
    resultSize: requestLimit,
    requestedTypes: [...DIRECTORY_SEARCH_REQUESTED_TYPES],
    requestId,
    queryTypes,
    includeCommonAreaPhones: true,
    includePersonalDevices: false,
    includeOnlyPairableDevices: false,
  };
}

/** Phone list plus a singular phoneNumber or extension field from a workspace entity. */
export function getRawPhoneEntries(entity: any): any[] {
  const listed = Array.isArray(entity?.phoneNumbers)
    ? entity.phoneNumbers
    : Array.isArray(entity?.phones)
      ? entity.phones
      : [];
  const entries = [...listed];
  if (typeof entity?.phoneNumber === 'string' && entity.phoneNumber.trim()) {
    entries.push({ type: 'work', value: entity.phoneNumber.trim() });
  }
  if (typeof entity?.extension === 'string' && entity.extension.trim()) {
    entries.push({ type: 'work_extension', value: entity.extension.trim() });
  }
  return entries;
}

function normalizeRoomType(entity: any): string {
  return String(entity?.roomType ?? '').replace(/\s+/g, '');
}

function isPersonalDevice(entity: any): boolean {
  return entity?.isPersonal === true || entity?.isPersonal === 'true';
}

function hasCallableNumber(entity: any): boolean {
  return getRawPhoneEntries(entity).some((entry) => {
    const value = typeof entry === 'string' ? entry : entry?.value ?? entry?.number;
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/**
 * People and calling services stay. A non-personal common-area phone stays.
 * A Lyra workspace stays only when it has an extension or phone number:
 * Directory Search labels a Webex Calling meeting-room workspace as
 * CI_MACHINE_LYRA_SPACE. Personal devices and other machines are dropped.
 */
function isDirectoryDeviceEntity(entity: any): boolean {
  const type = String(entity?.type ?? entity?.entityType ?? '').toUpperCase();
  const roomType = normalizeRoomType(entity);
  return type === 'ROOM' || roomType.startsWith('CI_MACHINE');
}

export function isIncludedDirectoryEntity(entity: any): boolean {
  if (!isDirectoryDeviceEntity(entity)) {
    return true;
  }
  const roomType = normalizeRoomType(entity);
  if (isPersonalDevice(entity)) {
    return false;
  }
  if (roomType === 'CI_MACHINE_LYRA_SPACE') {
    return hasCallableNumber(entity);
  }
  if (roomType === 'CI_MACHINE_ROOM') {
    return false;
  }
  if (roomType && roomType !== 'CI_MACHINE_COMMON_AREA_PHONE') {
    return false;
  }
  return true;
}

export function mapDssEntityToUser(entity: any): DirectorySearchUser {
  const rawPhones = getRawPhoneEntries(entity);
  const phones = rawPhones.map((p: any) =>
    typeof p === 'string' ? p : p?.value ?? p?.number ?? String(p)
  );
  const phoneNumbersWithType: DirectorySearchPhone[] = rawPhones
    .filter((p: any) => p && (p.value ?? p.number ?? (typeof p === 'string' ? p : null)))
    .map((p: any) => {
      const value = typeof p === 'string' ? p : p?.value ?? p?.number ?? String(p);
      const type = typeof p === 'object' && p.type != null ? normalizeDssPhoneType(String(p.type)) : 'work';
      return { type, value };
    });
  const structuredName = [
    entity.firstName,
    entity.lastName,
    entity.givenName,
    entity.familyName,
    entity.surname,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayName = (entity.displayName ?? entity.name ?? structuredName) || 'Unknown';
  const id = entity.id ?? entity.identity ?? entity.identityId ?? entity.userId ?? '';
  const avatarUrl = getDssPhotoUrl(entity.photos);
  const mailFromEmails = Array.isArray(entity.emails) && entity.emails.length > 0
    ? (entity.emails[0]?.value ?? entity.emails[0]?.address)
    : undefined;
  const mail = entity.email ?? entity.mail ?? mailFromEmails;
  const userPrincipalName = entity.userPrincipalName ?? entity.email ?? entity.mail ?? mailFromEmails;
  const mobileEntry = phoneNumbersWithType.find((p) => p.type === 'mobile');
  const businessPhoneValues = phoneNumbersWithType
    .filter((p) => isBusinessPhoneType(p.type))
    .map((p) => p.value);
  return {
    id,
    displayName,
    mail,
    userPrincipalName,
    mobilePhone: mobileEntry?.value ?? (phoneNumbersWithType.length === 0 ? phones[0] : undefined),
    businessPhones: businessPhoneValues.length > 0
      ? businessPhoneValues
      : (phoneNumbersWithType.length === 0 && phones.length > 1 ? phones.slice(1) : undefined),
    type: 'person',
    phones,
    ...(phoneNumbersWithType.length > 0 ? { phoneNumbersWithType } : {}),
    fromDss: true,
    ...(isDirectoryDeviceEntity(entity) ? { isWorkspaceDevice: true as const } : {}),
    ...(avatarUrl ? { avatarUrl } : {}),
  };
}

/**
 * Collects lowercase name tokens from a raw DSS entity for client-side filtering.
 * DSS NAME search matches userName, givenName, familyName, and displayName server-side;
 * the client filter must recognize the same field variants (SPARK-826003).
 */
function getDssEntityNameTokens(entity: any): string[] {
  const structured = [
    entity.firstName,
    entity.lastName,
    entity.middleName,
    entity.givenName,
    entity.familyName,
    entity.surname,
    entity.userName,
  ]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .map((part) => part.toLowerCase());

  if (structured.length > 0) {
    return structured;
  }

  const fullName = (entity.displayName ?? entity.name ?? '').toLowerCase();
  return fullName.split(/\s+/).filter(Boolean);
}

/** Max extra chars user may type beyond a stored name token (e.g. jungwirth vs jungwirt). */
const MAX_REVERSE_PREFIX_CHAR_GAP = 2;

/**
 * Scores how well a name token matches a query term (higher = better; 0 = no match).
 * Forward prefix: user still typing (token longer than query).
 * Reverse prefix: query extends stored token by a small suffix only (SPARK-843485 / BEMS02010203).
 * Short tokens (e.g. "jun" vs "jungwirth") must not match via reverse prefix.
 */
export function scoreNameTokenMatch(term: string, token: string): number {
  if (!term || !token) {
    return 0;
  }
  if (token === term) {
    return 100;
  }
  if (token.startsWith(term)) {
    return 80;
  }
  if (term.startsWith(token) && term.length - token.length <= MAX_REVERSE_PREFIX_CHAR_GAP) {
    return 80;
  }
  if (token.includes(term)) {
    return 50;
  }
  return 0;
}

/** True when query term matches a directory name token. */
export function nameTokenMatchesQuery(term: string, token: string): boolean {
  return scoreNameTokenMatch(term, token) > 0;
}

/** Sum of best per-term scores (0 if any term fails to match). */
export function scoreDssEntityNameMatch(entity: any, query: string): number {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return 0;
  }
  const nameTokens = getDssEntityNameTokens(entity);
  let total = 0;
  for (const term of terms) {
    const best = Math.max(0, ...nameTokens.map((token) => scoreNameTokenMatch(term, token)));
    if (best === 0) {
      return 0;
    }
    total += best;
  }
  return total;
}

/** Sort DSS entities so exact/prefix name matches appear before weaker substring hits. */
export function rankDssEntitiesByNameRelevance(entities: any[], query: string): any[] {
  const scored = entities.map((entity, index) => ({
    entity,
    index,
    score: scoreDssEntityNameMatch(entity, query),
  }));
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.map(({ entity }) => entity);
}

/**
 * Client-side name-part filter for DSS results.
 *
 * The DSS server performs full-text search and may return contacts where the
 * query term appears anywhere (e.g. department, title). This filter restricts
 * results to contacts where every query term is found in at least one name field
 * (firstName, lastName, givenName, familyName, surname, userName) or, when those
 * are absent, in a token of displayName / name.
 *
 * Only applied to NAME-type searches (not phone/extension lookups).
 */
export function filterDssEntitiesByNameParts(entities: any[], query: string): any[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return entities;

  return entities.filter((entity: any) => {
    const nameTokens = getDssEntityNameTokens(entity);
    return terms.every((term) => nameTokens.some((token) => nameTokenMatchesQuery(term, token)));
  });
}

function extractDssEntitiesFromEvent(event: any): any[] {
  const entities: any[] =
    event?.data?.directoryEntities ??
    event?.directoryEntities ??
    [];
  return Array.isArray(entities) ? entities : [];
}

function applyDssNameFilterAndRank(entities: any[], nameQuery: string): any[] {
  const before = entities.length;
  const filtered = filterDssEntitiesByNameParts(entities, nameQuery);
  const ranked = rankDssEntitiesByNameRelevance(filtered, nameQuery);
  Logger.info(`[DSS] Name-part filter queryLen=${nameQuery.length} before=${before} after=${ranked.length}`);
  return ranked;
}

function transformDssEntitiesToUsers(entities: any[], nameQuery?: string): DirectorySearchUser[] {
  const processed = nameQuery ? applyDssNameFilterAndRank(entities, nameQuery) : entities;
  return processed.map((entity: any) => mapDssEntityToUser(entity));
}


function getDssEntityDedupeKey(entity: any): string | undefined {
  const key =
    entity?.id || entity?.identity || entity?.identityId || entity?.userId || entity?.entityId;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}

function dedupeDssEntities(entities: any[]): any[] {
  const seen = new Set<string>();
  const deduped: any[] = [];
  for (const entity of entities) {
    const key = getDssEntityDedupeKey(entity);
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    deduped.push(entity);
  }
  return deduped;
}

function finalizeDssSearchResults(entities: any[], nameQuery?: string): DirectorySearchUser[] {
  const included = entities.filter((entity) => isIncludedDirectoryEntity(entity));
  return transformDssEntitiesToUsers(dedupeDssEntities(included), nameQuery);
}

/** Mercury event name for DSS Search API results (POST 202 → results via this event). */
const MERCURY_EVENT_DIRECTORY_SEARCH = 'event:directory.search';

const DSS_TIMEOUT_MS = 5000;

/**
 * Query type for DSS Search API body (queryTypes array).
 * Per Confluence "Phase 2: API: Entity Search" (WBXT) only NAME and PHONE are documented;
 * NAME = userName, givenName, familyName, displayName; PHONE = phoneNumber.
 */
export type DirectoryQueryType = 'NAME' | 'PHONE';

/**
 * Internal: call DSS Search API with given queryTypes. Same flow for PHONE and NAME.
 * @returns Directory users or [] on skip/error/timeout
 */
async function searchDirectory(
  query: string,
  limit: number,
  queryTypes: DirectoryQueryType[]
): Promise<DirectorySearchUser[]> {
  return new Promise((resolve) => {
    (async () => {
      try {
        const orgId = getOrgIdFromContext();
        const baseUrl = await getDirectoryBaseUrlAsync();
        const webex = getWebexInstance();

        if (!baseUrl || !orgId) {
          Logger.info('[DSS] Skipping Search API: missing directory base URL or orgId');
          resolve([]);
          return;
        }

        if (!webex) {
          Logger.error('[DSS] Webex instance not available');
          resolve([]);
          return;
        }

        const mercury = webex.internal?.mercury;
        if (!mercury) {
          Logger.error('[DSS] Mercury not available');
          resolve([]);
          return;
        }

        const trimmed = query.trim();
        if (!trimmed) {
          resolve([]);
          return;
        }

        const requestId = crypto.randomUUID();
        const requestLimit = Math.min(Math.max(1, limit), 100);

        // Apply name-part filtering only for pure NAME queries (not phone lookups).
        const nameOnlyQuery = queryTypes.length === 1 && queryTypes[0] === 'NAME' ? trimmed : undefined;

        const accumulatedEntities: any[] = [];
        const finishSearch = (): DirectorySearchUser[] =>
          finalizeDssSearchResults(accumulatedEntities, nameOnlyQuery);

        let timeout: ReturnType<typeof setTimeout>;

        const handler = (event: any) => {
          const eventRequestId = event?.data?.requestId ?? event?.requestId;
          if (eventRequestId !== requestId) return;

          const batchEntities = extractDssEntitiesFromEvent(event);
          accumulatedEntities.push(...batchEntities);
          Logger.info(`[DSS] Mercury ${MERCURY_EVENT_DIRECTORY_SEARCH} requestId=${requestId} rawBatch=${batchEntities.length} totalRaw=${accumulatedEntities.length} finished=${!!event?.data?.finished}`);

          if (event?.data?.finished) {
            clearTimeout(timeout);
            mercury.off(MERCURY_EVENT_DIRECTORY_SEARCH, handler);
            resolve(finishSearch());
          }
        };

        timeout = setTimeout(() => {
          mercury.off(MERCURY_EVENT_DIRECTORY_SEARCH, handler);
          Logger.error(`[DSS] Search API timeout (requestId=${requestId})`);
          resolve(finishSearch());
        }, DSS_TIMEOUT_MS);

        mercury.on(MERCURY_EVENT_DIRECTORY_SEARCH, handler);

        const body = buildDirectorySearchRequestBody(trimmed, requestLimit, requestId, queryTypes);
        const uri = `${baseUrl}/search/orgid/${orgId}/entities`;
        Logger.info(`[DSS] Search API request requestId=${requestId} query="${trimmed}" queryTypes=${JSON.stringify(queryTypes)} uri=${uri}`);

        const onRequestFailure = (err: any) => {
          clearTimeout(timeout);
          mercury.off(MERCURY_EVENT_DIRECTORY_SEARCH, handler);
          const status = err?.statusCode ?? err?.status ?? err?.response?.status;
          const message = err?.message ?? err?.body?.message ?? String(err);
          const bodyText = typeof err?.body === 'string'
            ? err.body
            : err?.body ? JSON.stringify(err.body) : '';
          Logger.error(`[DSS] Search API failed status=${status} message=${message}${bodyText ? ` body=${bodyText.slice(0, 500)}` : ''}`);
          resolve([]);
        };

        try {
          const response = await webex.request({ method: 'POST', uri, body });

          const status = response?.statusCode ?? response?.status ?? (response as any)?.status;
          if (status != null && status >= 400) {
            onRequestFailure({ statusCode: status, body: response?.body ?? response });
            return;
          }
          if (status === 202 || status === 200) {
            Logger.info(`[DSS] Search API accepted (${status}), waiting for Mercury ${MERCURY_EVENT_DIRECTORY_SEARCH}`);
          }
        } catch (err: any) {
          onRequestFailure(err);
        }
      } catch (error: any) {
        Logger.error(`[DSS] Error: ${error?.message ?? error}`);
        resolve([]);
      }
    })();
  });
}

/**
 * Look up directory users by phone number or extension via DSS Search REST API.
 * Sends POST with queryTypes: ['PHONE']; API returns 202 and results arrive via Mercury event:directory.search.
 *
 * @param query – phone or extension string (e.g. "8732", "98234455")
 * @param limit – max results (capped at 100)
 * @returns Directory users matching the query (empty array if config/Webex unavailable or error)
 */
export async function lookupByPhoneOrExtension(
  query: string,
  limit = 100
): Promise<DirectorySearchUser[]> {
  return searchDirectory(query, limit, ['NAME', 'PHONE']);
}

/**
 * Look up directory users by name or email via DSS Search REST API.
 * Per Confluence "Phase 2: API: Entity Search", only queryTypes NAME and PHONE are defined;
 * EMAIL is not in the spec. We send queryTypes: ['NAME'] for both name and email so the
 * request is spec-compliant (DSS may match email in userName/displayName; else we fall back to Graph).
 * Same flow as phone: POST 202 + results via Mercury event.
 *
 * @param query – name or email search string (e.g. "alex@", "John")
 * @param limit – max results (capped at 100)
 * @param _queryType – caller hint 'NAME'|'EMAIL'; we always send ['NAME'] to DSS (spec has no EMAIL type)
 * @returns Directory users matching the query (empty array if skip/error/timeout)
 */
export async function lookupByNameOrEmail(
  query: string,
  limit: number,
  _queryType: 'NAME' | 'EMAIL'
): Promise<DirectorySearchUser[]> {
  return searchDirectory(query, limit, ['NAME']);
}
